import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { service } from '@ember/service'
import { htmlSafe } from '@ember/template'
import { t, type IntlService } from 'ember-intl'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type { Log } from '@blakeelearning/log'
import { trackedFunction } from 'reactiveweb/function'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import { defaultProductForSubscriptionType } from 'teacher-ui-client/utils/products'
import WidgetCard, { WidgetContent, WidgetNoDataMessage } from 'teacher-ui-client/components/teacher-home/widget-card'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { gt, lte, or } from 'ember-truth-helpers'

export interface UpcomingAssignmentsWeek {
  id: string
  week_starting_on: string
  week_ending_on: string
  complete_assignments_count: number
  incomplete_assignments_count: number
  complete_tasks_count: number
  incomplete_tasks_count: number
  active_students_count: number
  inactive_students_count: number
}

export interface UpcomingAssignmentsResponse {
  upcoming: (UpcomingAssignmentsWeek | null)[]
}

interface Signature {
  Element: HTMLElement
}

export class TeacherHomeUpcomingAssignments extends Component<Signature> {
  @service log!: Log
  @service authToken!: AuthToken
  @service declare subscriptionType: SubscriptionTypeService

  fetchData = trackedFunction(this, async () => {
    try {
      const url = join(
        config.gravityV3Url,
        `teacher-ui/widgets/upcoming-assignments/${this.subscriptionType.current}`,
      ).href
      const headers = {
        Authorization: this.authToken.token!,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }
      const response = await fetch(url, {
        method: 'GET',
        headers,
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch upcoming assignments: ${response.status}`)
      }

      return response.json() as Promise<UpcomingAssignmentsResponse>
    } catch (err) {
      this.log.error('failed to fetch upcoming assignments widget data', err ?? undefined)
      throw err
    }
  })

  get weekData(): Nullable<{ current: UpcomingAssignmentsWeek; next: UpcomingAssignmentsWeek }> {
    const current = this.fetchData.value?.upcoming?.[0]
    const next = this.fetchData.value?.upcoming?.[1]
    return current && next ? { current, next } : null
  }

  <template>
    <WidgetCard
      @title={{t "teacherHome.widgets.upcomingAssignments.title"}}
      @borderAccentClass="border-t-juicy-orange-300"
      data-test-teacher-home-widget-card="upcoming-assignments"
      ...attributes
    >
      {{#if this.fetchData.isPending}}
        <LoadingState />
      {{else if this.weekData}}
        <WidgetContent class="divide-dusty-black-100 flex h-full flex-col gap-2 divide-y">
          <WeekSection
            data-test-current-week
            @week="current"
            @weekData={{this.weekData.current}}
            @subscriptionType={{this.subscriptionType.current}}
          />
          <WeekSection
            data-test-next-week
            @week="next"
            @weekData={{this.weekData.next}}
            @subscriptionType={{this.subscriptionType.current}}
          />

        </WidgetContent>
      {{else}}
        <WidgetNoDataMessage />
      {{/if}}
    </WidgetCard>
  </template>
}

// WEEK SECTION

interface WeekSectionSignature {
  Element: HTMLDivElement
  Args: {
    week: 'current' | 'next'
    weekData: UpcomingAssignmentsWeek
    subscriptionType: SubscriptionType
  }
}

class WeekSection extends Component<WeekSectionSignature> {
  @service intl!: IntlService

  get assignmentTotals() {
    return this.args.weekData.complete_assignments_count + this.args.weekData.incomplete_assignments_count
  }

  get studentsTotals() {
    return this.args.weekData.active_students_count + this.args.weekData.inactive_students_count
  }

  get productRoute(): string {
    return defaultProductForSubscriptionType(this.args.subscriptionType)
  }

  get hasNoData() {
    return this.args.weekData.active_students_count === 0 && this.args.weekData.inactive_students_count === 0
  }

  get label() {
    return this.args.week === 'current'
      ? this.intl.t('teacherHome.widgets.upcomingAssignments.currentWeek')
      : this.intl.t('teacherHome.widgets.upcomingAssignments.nextWeek')
  }

  get weekPeriodForNoDataLabel() {
    return this.args.week === 'current'
      ? this.intl.t('teacherHome.widgets.upcomingAssignments.noDataCurrentWeek')
      : this.intl.t('teacherHome.widgets.upcomingAssignments.noDataNextWeek')
  }

  <template>
    <div class="flex h-1/2 flex-col gap-2" ...attributes>
      <p class="font-medium text-gray-900">{{this.label}}</p>
      {{#unless this.hasNoData}}
        <div class="space-y-2 px-2.5">
          <ProgressBar
            @label={{t
              "teacherHome.widgets.upcomingAssignments.assignmentsCompleted"
              completed=@weekData.complete_assignments_count
              total=this.assignmentTotals
            }}
            @count={{@weekData.complete_assignments_count}}
            @total={{this.assignmentTotals}}
            data-test-assignments-metric
          />
          <ProgressBar
            @label={{t
              "teacherHome.widgets.upcomingAssignments.studentsSubmitted"
              active=@weekData.active_students_count
              total=this.studentsTotals
            }}
            @count={{@weekData.active_students_count}}
            @total={{this.studentsTotals}}
            data-test-students-metric
          />
        </div>
      {{else}}
        <p class="px-2.5">{{this.weekPeriodForNoDataLabel}}</p>
      {{/unless}}
    </div>
  </template>
}

// PROGRESS BAR

interface ProgressBarSignature {
  Element: HTMLDivElement
  Args: {
    label: string
    count: number
    total: number
  }
}

const MAX_PIPS = 50
const PIP_GAP_THRESHOLD = 26

interface PipCount {
  completed: unknown[]
  leftover: unknown[]
}

function widthPercentage(perc: number) {
  return htmlSafe(`width: ${perc}%`)
}

class ProgressBar extends Component<ProgressBarSignature> {
  get percent(): number {
    if (this.args.total <= 0) return 100
    return Math.min(100, Math.round((this.args.count / this.args.total) * 100))
  }

  get toneClass(): string {
    if (this.percent <= 49) return 'bg-watermelony-red-350'
    if (this.percent <= 79) return 'bg-juicy-orange-300'
    return 'bg-ms-green-300'
  }

  get pipCounts(): PipCount {
    return {
      completed: Array.from({ length: this.args.count }),
      leftover: Array.from({ length: this.args.total - this.args.count }),
    }
  }

  get countStyle() {
    return widthPercentage(this.percent)
  }

  get remainderPercent(): number {
    return 100 - this.percent
  }

  get hasRemainder(): boolean {
    return this.remainderPercent > 0
  }

  get remainderStyle() {
    return widthPercentage(this.remainderPercent)
  }

  <template>
    <div ...attributes>
      <div class="mb-2">{{@label}}</div>
      {{#if (or (lte @total 0) (gt @total MAX_PIPS))}}
        <div class="relative flex flex-nowrap">
          <span
            class="h-5 {{if this.hasRemainder 'rounded-s-sm' 'rounded-sm'}} {{this.toneClass}}"
            style={{this.countStyle}}
          ></span>
          {{#if this.hasRemainder}}
            <span class="h-5 rounded-e-sm bg-neutral-50" style={{this.remainderStyle}}></span>
          {{/if}}
        </div>
      {{else}}
        <div class="flex flex-nowrap {{if (gt @total PIP_GAP_THRESHOLD) 'gap-0.5' 'gap-1'}}">
          {{#each this.pipCounts.completed}}
            <span class="h-5 w-3 max-w-3 min-w-0.5 rounded-sm {{this.toneClass}}"></span>
          {{/each}}
          {{#each this.pipCounts.leftover}}
            <span class="h-5 w-3 max-w-3 min-w-0.5 rounded-sm bg-neutral-50"></span>
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}

const LoadingState: TOC<Signature> = <template>
  <WidgetContent class="divide-dusty-black-100 flex h-full animate-pulse flex-col gap-2 divide-y">
    <LoadingWeekSection data-test-current-week-loading />
    <LoadingWeekSection data-test-next-week-loading />
  </WidgetContent>
</template>

const LoadingWeekSection: TOC<Signature> = <template>
  <div class="flex h-1/2 flex-col gap-2" ...attributes>
    <div class="bg-dusty-black-100 mb-2 h-5 w-32 animate-pulse rounded"></div>
    <div class="space-y-2 px-2.5">
      <LoadingProgressBar />
      <LoadingProgressBar />
    </div>
  </div>
</template>

const LoadingProgressBar: TOC<Signature> = <template>
  <div>
    <div class="bg-dusty-black-100 h-5 w-64 animate-pulse rounded" />
  </div>
  <div class="relative flex flex-nowrap">
    <div class="bg-dusty-black-100 h-5 w-full animate-pulse rounded-sm" />
  </div>
</template>

export default TeacherHomeUpcomingAssignments
